import {PartyType} from '@sollapay/enums';
import {cn} from '@sollapay/ui';
import {
  Badge,
  Button,
  ButtonGroup,
  FieldDatePicker,
  FieldInput,
  FieldLabel,
  FieldRequiredLabel,
  FieldSelect,
  Icon,
  Tooltip,
  Typography
} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {DateCell} from '@/components';
import {AddressAutocompleteField} from '@/features/address';

import {BeneficiaryMoreMenu} from './BeneficiaryMoreMenu';
import {computeDraftErrors, draftFromRow, fieldElementId} from './utils/beneficiary-draft.utils';

import type {
  BeneficiaryRow,
  DraftBeneficiary,
  DraftErrorCode,
  DraftField
} from './types/beneficiary-draft.types';
import type {PartyRole} from '@sollapay/enums';
import type {KeyboardEvent} from 'react';

export interface EntityTypeCopy {
  toggleLabel: string;
  nameLabel: string;
  namePlaceholder: string;
  idLabel: string;
  idPlaceholder: string;
  idHint: string;
  dateLabel: string;
}

export type EntityTypeCopyMap = Record<PartyType, EntityTypeCopy>;

interface FieldChangeProps {
  onFieldChange: (field: DraftField, value: DraftBeneficiary[DraftField]) => void;
}

interface FieldErrorProps {
  erroredFields: Set<DraftField>;
  errorMessage: (code: DraftErrorCode | undefined) => string | undefined;
}

export function BeneficiaryNameCell({
  row,
  entityTypeCopy,
  onFieldChange,
  onKeyDown,
  erroredFields,
  errorMessage
}: FieldChangeProps &
  FieldErrorProps & {
    row: BeneficiaryRow;
    entityTypeCopy: EntityTypeCopyMap;
    onKeyDown: (e: KeyboardEvent) => void;
  }) {
  if (row.isDraft || row.isEditing) {
    const copy = entityTypeCopy[row.entityType];
    return (
      <div className="flex flex-col gap-1.5">
        <FieldLabel htmlFor={fieldElementId('name')}>
          <FieldRequiredLabel required>{copy.nameLabel}</FieldRequiredLabel>
        </FieldLabel>
        <div className="flex items-start gap-2">
          <ButtonGroup
            items={[
              {
                value: PartyType.PERSON,
                label: entityTypeCopy[PartyType.PERSON].toggleLabel,
                icon: 'user'
              },
              {
                value: PartyType.ORGANIZATION,
                label: entityTypeCopy[PartyType.ORGANIZATION].toggleLabel,
                icon: 'building'
              }
            ]}
            value={row.entityType}
            onChange={value => onFieldChange('entityType', value as PartyType)}
            disabled={row.isEditing}
            size="sm"
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <FieldInput
              id={fieldElementId('name')}
              value={row.name}
              onChange={e => onFieldChange('name', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={copy.namePlaceholder}
              error={erroredFields.has('name') ? errorMessage('required') : undefined}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'flex size-6.5 shrink-0 items-center justify-center rounded-md',
          row.entityType === PartyType.ORGANIZATION
            ? 'bg-indication-info/[14%] text-text-info'
            : 'bg-background-tertiary text-fg-quaternary'
        )}
      >
        <Icon
          name={row.entityType === PartyType.ORGANIZATION ? 'building' : 'user'}
          className="size-3.5"
        />
      </span>
      <Typography weight="semibold" size="sm" className="min-w-0 truncate">
        {row.name}
      </Typography>
    </div>
  );
}

export function BeneficiaryRoleCell({
  row,
  isManual,
  roleOptions,
  onFieldChange,
  erroredFields,
  errorMessage
}: FieldChangeProps &
  FieldErrorProps & {
    row: BeneficiaryRow;
    isManual: boolean;
    roleOptions: {value: string; label: string}[];
  }) {
  const {t} = useTranslation('trustAccounts');

  if (row.isDraft || row.isEditing) {
    return (
      <FieldSelect
        id={fieldElementId('role')}
        label={t('common:fields.role.label')}
        required
        value={row.role || undefined}
        options={roleOptions}
        placeholder={t('common:fields.role.placeholder')}
        onValueChange={value => onFieldChange('role', value as PartyRole)}
        error={erroredFields.has('role') ? errorMessage('required') : undefined}
        fieldClassName="*:w-fit"
        triggerClassName="w-fit"
        align="start"
      />
    );
  }

  return (
    <Badge variant={isManual ? 'brand-subtle' : 'neutral'} size="sm">
      {t(`partyRole.${row.role}`)}
    </Badge>
  );
}

export function BeneficiaryIdentifierCell({
  row,
  entityTypeCopy,
  isDuplicate,
  onFieldChange,
  onKeyDown,
  erroredFields,
  errorMessage
}: FieldChangeProps &
  FieldErrorProps & {
    row: BeneficiaryRow;
    entityTypeCopy: EntityTypeCopyMap;
    isDuplicate: boolean;
    onKeyDown: (e: KeyboardEvent) => void;
  }) {
  const {t} = useTranslation('trustAccounts');

  if (row.isDraft || row.isEditing) {
    const copy = entityTypeCopy[row.entityType];
    const idError = computeDraftErrors(draftFromRow(row)).idNumber;
    const duplicateWarning = isDuplicate ? t('view.beneficiaries.duplicateIdWarning') : undefined;
    const fieldError = erroredFields.has('idNumber') ? errorMessage(idError) : duplicateWarning;

    return (
      <FieldInput
        id={fieldElementId('idNumber')}
        label={copy.idLabel}
        required
        startAdornment={<Icon name="hash" className="size-4" />}
        value={row.idNumber}
        onChange={e => onFieldChange('idNumber', e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={copy.idPlaceholder}
        error={fieldError}
        description={fieldError ? undefined : copy.idHint}
      />
    );
  }

  return (
    <Typography size="sm" className="tabular-nums">
      {row.idNumber}
    </Typography>
  );
}

export function BeneficiaryDateCell({
  row,
  entityTypeCopy,
  onFieldChange,
  erroredFields,
  errorMessage
}: FieldChangeProps &
  FieldErrorProps & {
    row: BeneficiaryRow;
    entityTypeCopy: EntityTypeCopyMap;
  }) {
  const {t} = useTranslation('trustAccounts');

  if (row.isDraft || row.isEditing) {
    const copy = entityTypeCopy[row.entityType];
    const dateError = computeDraftErrors(draftFromRow(row)).date;
    return (
      <FieldDatePicker
        id={fieldElementId('date')}
        label={copy.dateLabel}
        required
        disableFuture
        value={row.date || undefined}
        onChange={value => onFieldChange('date', value)}
        placeholder={t('view.beneficiaries.placeholders.date')}
        error={erroredFields.has('date') ? errorMessage(dateError) : undefined}
      />
    );
  }

  return <DateCell date={row.date} />;
}

export function BeneficiaryAddressCell({
  row,
  onFieldChange,
  onKeyDown,
  erroredFields,
  errorMessage
}: FieldChangeProps &
  FieldErrorProps & {
    row: BeneficiaryRow;
    onKeyDown: (e: KeyboardEvent) => void;
  }) {
  const {t} = useTranslation('trustAccounts');

  if (row.isDraft || row.isEditing) {
    return (
      <AddressAutocompleteField
        id={fieldElementId('address')}
        label={t('common:fields.address.label')}
        required
        value={row.address}
        onChange={address => onFieldChange('address', address)}
        onKeyDown={onKeyDown}
        error={erroredFields.has('address') ? errorMessage('required') : undefined}
      />
    );
  }

  return row.address ? (
    <Typography size="sm" className="truncate" title={row.address}>
      {row.address}
    </Typography>
  ) : (
    <Typography size="sm" color="tertiary">
      {t('view.beneficiaries.notApplicable')}
    </Typography>
  );
}

export function BeneficiaryActionsCell({
  row,
  isManual,
  isConfirmingDraft,
  isConfirmingEdit,
  onSave,
  onCancelDraft,
  onCancelEdit,
  onStartEdit,
  onRemoveRow
}: {
  row: BeneficiaryRow;
  isManual: boolean;
  isConfirmingDraft?: boolean;
  isConfirmingEdit?: boolean;
  onSave: (row: BeneficiaryRow) => void;
  onCancelDraft?: () => void;
  onCancelEdit?: () => void;
  onStartEdit?: (row: BeneficiaryRow) => void;
  onRemoveRow?: (partyId: string) => void;
}) {
  const {t} = useTranslation('trustAccounts');

  if (row.isDraft || row.isEditing) {
    const isPending = row.isDraft ? isConfirmingDraft : isConfirmingEdit;
    const saveLabel = row.isDraft
      ? t('view.beneficiaries.tableActions.add')
      : t('view.beneficiaries.tableActions.save');
    return (
      <div className="flex flex-col gap-1.5">
        <span aria-hidden className="invisible text-sm leading-snug">
          &nbsp;
        </span>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="primary"
            size="sm"
            icon={<Icon name="check" />}
            isLoading={isPending}
            onClick={() => onSave(row)}
          >
            {saveLabel}
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            disabled={isPending}
            onClick={row.isDraft ? onCancelDraft : onCancelEdit}
          >
            {t('view.beneficiaries.tableActions.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  const {partyId} = row;
  if (!partyId) return null;

  if (!isManual) {
    return (
      <div className="flex items-center justify-end">
        <Tooltip content={t('view.beneficiaries.lockedTooltip')}>
          <Badge variant="neutral" size="sm" className="text-fg-tertiary">
            <Icon name="lock" className="size-3.5" />
          </Badge>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex justify-end">
      <BeneficiaryMoreMenu
        actions={['edit', 'delete']}
        onEdit={onStartEdit ? () => onStartEdit(row) : undefined}
        onDelete={onRemoveRow ? () => onRemoveRow(partyId) : undefined}
      />
    </div>
  );
}
